import {
  Button, Form, Input, Radio, Select, Switch, Upload, message,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useCallback, useMemo, useState } from 'react';
import { UploadFile } from 'antd/lib/upload/interface';
import { useMutation } from 'react-query';

import { uploadFile } from 'services/storage.service';
import ValueForm from '../../Product/Create/components/ValueForm';
import styles from '../../Product/Create/components/CreateProduct/index.module.scss';
import TranslateFormWrapper from '../TranslationForm';

export interface ProductFormProps {
  onFinish: (form: any, imageUrl: string) => void;
  categories: Category[];
  product?: Product.Item;
  type: string;
  formRef?: any;
}

export const ProductForm = ({
  categories, onFinish, formRef, product, type,
}: ProductFormProps) => {
  const currentImage = product?.values?.[0]?.assets?.find((a) => a.type === 'img')?.src || product?.image || '';

  const fileList = product ? [{
    uid: '-1',
    name: 'image.png',
    status: 'done',
    url: currentImage,
  }] : [];

  const [selectedCategory, setSelectedCategory] = useState<Category>(product?.category!);
  const [imageMode, setImageMode] = useState<'upload' | 'url'>('upload');
  const [imageUrl, setImageUrl] = useState(currentImage);
  const [uploadedUrl, setUploadedUrl] = useState<string>(currentImage);
  const [isUploading, setIsUploading] = useState(false);
  const { mutateAsync: upload } = useMutation(uploadFile);

  const handleSetFile = useCallback(async (e: any) => {
    const selectedFile = e.file;
    if (!selectedFile?.originFileObj) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append('image', selectedFile.originFileObj);
    const safeName = (selectedFile.name || 'product')
      .replace(/\.[^/.]+$/, '')
      .replace(/[^a-zA-Z0-9_-]/g, '_');
    formData.append('path', `product/${safeName}_${Date.now()}`);

    try {
      const result = await upload(formData);
      setUploadedUrl(result.data);
      message.success('Зображення завантажено');
    } catch (err: any) {
      message.error(err?.message || 'Помилка завантаження зображення');
    } finally {
      setIsUploading(false);
    }
  }, [upload]);

  const handlerSelectCategory = useCallback((categoryName: string) => {
    const category = categories.find((c) => c.name === categoryName);
    if (category) setSelectedCategory(category);
  }, [categories]);

  const buttonTitle = useMemo(() => (
    type === 'edit' ? 'Update product' : 'Create product'
  ), [type]);

  const handleFinish = useCallback((form: any) => {
    let finalImage = uploadedUrl || product?.image || '';
    if (imageMode === 'url') {
      finalImage = imageUrl;
    }
    onFinish(form, finalImage);
  }, [imageMode, imageUrl, uploadedUrl, product, onFinish]);

  return (
    <Form onFinish={handleFinish} form={formRef}>
      <Form.Item label='Category' name='category'>
        <Select
          className={styles.categories}
          placeholder='Categories'
          options={categories && categories.map((c) => ({ value: c.name }))}
          onChange={handlerSelectCategory}
        />
      </Form.Item>
      {selectedCategory && <ValueForm category={selectedCategory} />}
      <Form.Item label='Product name' name='name'>
        <Input placeholder='Product name' />
      </Form.Item>
      <Form.Item name='visible' label='All values visible' valuePropName='checked'>
        <Switch />
      </Form.Item>
      <Form.Item name='overpriced' label='Overpriced' valuePropName='checked' initialValue>
        <Switch />
      </Form.Item>
      <Form.Item label='Original name' name='originalName'>
        <Input placeholder='originalName' />
      </Form.Item>
      <Form.Item label='description' name='description'>
        <Input placeholder='description' />
      </Form.Item>
      <Form.Item label='How to use' name='howToUse'>
        <Input placeholder='How to use' />
      </Form.Item>
      <Form.Item label='Image'>
        <Radio.Group
          value={imageMode}
          onChange={(e) => setImageMode(e.target.value)}
          style={{ marginBottom: 8, display: 'block' }}
        >
          <Radio.Button value='upload'>Upload file</Radio.Button>
          <Radio.Button value='url'>Image URL</Radio.Button>
        </Radio.Group>
        {imageMode === 'upload' ? (
          <>
            <Upload
              name='file'
              beforeUpload={() => false}
              onChange={handleSetFile}
              defaultFileList={fileList as UploadFile<any>[]}
              showUploadList={{ showRemoveIcon: false }}
            >
              <Button icon={<UploadOutlined />} loading={isUploading}>
                {isUploading ? 'Завантаження...' : 'Click to Upload'}
              </Button>
            </Upload>
            {uploadedUrl && uploadedUrl !== product?.image && (
              <div style={{ marginTop: 8 }}>
                <img src={uploadedUrl} alt='preview' style={{ maxHeight: 80, maxWidth: 200 }} />
              </div>
            )}
          </>
        ) : (
          <Input
            placeholder='https://example.com/image.jpg'
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
          />
        )}
      </Form.Item>
      <TranslateFormWrapper
        field={{ name: 'root', key: 'root' }}
        items={[
          {
            name: 'name',
            item: <Input placeholder='name translation' />,
          },
          {
            name: 'description',
            item: <Input placeholder='description translation' />,
          },
        ]}
      />
      <Form.Item>
        <Button
          className={styles.createButton}
          type='primary'
          htmlType='submit'
          disabled={isUploading}
        >
          {buttonTitle}
        </Button>
      </Form.Item>
    </Form>
  );
};
